import React, { useState } from "react";

export default function SMRTDocsFileHistory({
  isOpen,
  onClose,
  history = [],
  title,
  onDelete,
  onView,
  // Backend URL for the Certificate of Completion (server-generated,
  // Content-Disposition: attachment). Rendered as a real <a href> — the
  // download pattern that survives an RBI / managed browser, where a
  // callback + synthetic click silently does nothing.
  certificateUrl,
  isUserView = false,
  // Document type the history belongs to. Only "template" docs go
  // through a sign / countersign flow and therefore have a paired
  // unsigned snapshot worth exposing via View Original. Certificate
  // and Info documents skip the button entirely.
  documentType,
}: any) {
  const [openRow, setOpenRow] = useState<number | null>(null);
  // Which row's kebab (⋮) menu is currently expanded. The expanded
  // menu floats horizontally outward (absolute positioning) so it can
  // stretch beyond the history modal's right edge — same pattern as
  // SMRT Tasks.
  const [menuRow, setMenuRow] = useState<number | null>(null);
  if (!isOpen) return null;

  return (
    <div className="smrtdocs-modal-overlay">
      <div
        className="smrtdocs-modal"
        style={{ width: "900px", maxWidth: "95%" }}
      >
        <div className="smrtdocs-modal-header">
          <h2>{title ? `${title} - File History` : "File History"}</h2>
          <button className="btn-cancel" onClick={onClose}>
            Back
          </button>
        </div>

        <div className="smrtdocs-modal-body">
          <div className="smrtdocs-history-header">
            <div>Last Updated</div>
            <div>Updated By</div>
            <div>File</div>
            <div>Status</div>
            <div>Actions</div>
          </div>

          {history.length === 0 && (
            <div style={{ padding: "10px", color: "#999" }}>
              No history available
            </div>
          )}

          {[...history].reverse().map((item: any, i: number) => {
            if (!item?.file) return null;
            const originalIndex = history.length - 1 - i;
            // Skip unsigned snapshots — each signing cycle would
            // otherwise render as two rows pointing at the same signed
            // file (because the display logic above remaps unsigned
            // rows to their paired signed counterpart). The unsigned
            // entry stays in the underlying array so the green
            // "View Original" lookup on the signed row can still
            // find it.
            if (item?.kind === "unsigned") return null;
            // Locate the unsigned snapshot paired with this row so the
            // "View Original" action can open the pre-signing version.
            //
            //  1. Preferred: a prior entry explicitly tagged
            //     `kind: "unsigned"` (set on new send-time snapshots).
            //  2. Fallback for legacy / archived rows that pre-date the
            //     `kind` marker — the most recent prior entry whose
            //     file *name differs* from this row's. A differing
            //     file name implies a separate version (unsigned vs.
            //     signed), which is enough to retroactively surface a
            //     View Original button on older archived cycles.
            //
            // The fallback is skipped for unsigned-kind rows so the
            // green button on a deliberately-unsigned row points back
            // at this row's own file (handled below), not the prior
            // cycle's source.
            let originalFile: any = null;
            for (let j = originalIndex - 1; j >= 0; j--) {
              const h = (history as any[])[j];
              if (h?.kind === "unsigned" && h?.file) {
                originalFile = h.file;
                break;
              }
            }
            if (!originalFile && item?.kind !== "unsigned") {
              const currentFileName = item?.file?.name || "";
              for (let j = originalIndex - 1; j >= 0; j--) {
                const h = (history as any[])[j];
                if (h?.file?.name && h.file.name !== currentFileName) {
                  originalFile = h.file;
                  break;
                }
              }
            }
            // The displayed file (and clickable link target) should
            // always be the SIGNED version of the document for the
            // cycle this row represents.
            //
            //  - "Signed-ness" is detected via `kind: "signed"` on
            //    newer entries, or by the `_signed` suffix the signed
            //    PDF upload writes into the file name (e.g.
            //    "Test Doc (6)_signed.pdf"). Either marker satisfies
            //    the lookup so legacy archived rows work too.
            //
            //  - For rows that themselves look unsigned (no `_signed`
            //    in the name and not explicitly `kind: "signed"`), we
            //    walk forward through the chronological array for the
            //    next entry that appears to be signed and use its
            //    file. Falls back to the row's own file if no signed
            //    entry has been recorded yet (mid-cycle).
            let fileObj = item.file;
            const currentName = String(item?.file?.name || "");
            const looksSigned =
              item?.kind === "signed" ||
              /_signed/i.test(currentName);
            if (!looksSigned) {
              for (
                let j = originalIndex + 1;
                j < (history as any[]).length;
                j++
              ) {
                const h = (history as any[])[j];
                if (!h?.file) continue;
                // Cycle boundary: a later `kind: "unsigned"` entry
                // belongs to the NEXT signing cycle. Stop here so an
                // archived row from cycle N never picks up cycle N+1's
                // signed file (the bug behind "previous version not
                // showing as Archived" — the row was correct but its
                // file link pointed at the active cycle's PDF).
                if (h.kind === "unsigned") break;
                const hName = String(h.file.name || "");
                if (h.kind === "signed" || /_signed/i.test(hName)) {
                  fileObj = h.file;
                  break;
                }
              }
              if (!originalFile && item.file) originalFile = item.file;
            }

            return (
              <div key={i}>
                {/* ROW */}
                <div className="smrtdocs-history-row">
                  <div>
                    {item.approvedAt ||
                    item.submittedAt ||
                    item.archivedAt ||
                    item.date
                      ? new Date(
                          item.approvedAt ||
                            item.submittedAt ||
                            item.archivedAt ||
                            item.date,
                        ).toLocaleString()
                      : "—"}
                  </div>

                  <div>{isUserView
                    ? (item.approvedBy ? "Admin" : (item.status === "Client Submitted" ? (item.submittedBy || "You") : "Admin"))
                    : (item.approvedBy || item.submittedBy || "—")}</div>

                  <div
                    className="smrtdocs-file-link"
                    onClick={() => {
                      if (!fileObj) return;
                      if (onView) { onView(fileObj); return; }
                      alert("Preview unavailable");
                    }}
                  >
                    {fileObj?.name || "Unknown File"}
                  </div>

                  <div>{item.status || "Pending"}</div>

                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      flexWrap: "nowrap",
                      // Pin to the grid track's left edge and let the
                      // expanded buttons paint to the right of the
                      // cell. The track stays 14ch (matches the
                      // closed-state width); the extra buttons can
                      // overflow the modal entirely rather than
                      // pushing the cell wider.
                      width: "max-content",
                    }}
                  >
                    {/* Blue 👁 — always visible. Toggles the per-row
                        audit details expand below. */}
                    <button
                      className="btn-icon smrtdocs-btn-view"
                      title="View"
                      onClick={() => {
                        setOpenRow(openRow === i ? null : i);
                        setMenuRow(null);
                      }}
                    >
                      👁
                    </button>

                    {/* Secondary actions only appear once the kebab is
                        opened. Same SMRT-Tasks pattern: the buttons
                        line up inline alongside the blue View button
                        and the ⋮ moves to the end of the row. */}
                    {menuRow === i && onDelete && (
                      <button
                        className="btn-icon btn-toggle-delete"
                        title="Delete"
                        onClick={() => {
                          onDelete(history.length - 1 - i);
                          setMenuRow(null);
                        }}
                      >
                        🗑
                      </button>
                    )}
                    {menuRow === i &&
                      documentType === "template" &&
                      originalFile && (
                        <button
                          className="btn-icon"
                          title="View Original"
                          onClick={() => {
                            if (onView) onView(originalFile);
                            else alert("Preview unavailable");
                            setMenuRow(null);
                          }}
                          style={{
                            background: "#1faa59",
                            borderColor: "#1faa59",
                            color: "#fff",
                          }}
                        >
                          👁
                        </button>
                      )}
                    {menuRow === i && certificateUrl && (
                      <a
                        className="btn-icon"
                        title="Certificate of Completion"
                        href={certificateUrl}
                        onClick={() => setMenuRow(null)}
                        style={{
                          background: "#f3f4f6",
                          borderColor: "#d1d5db",
                          color: "#1f2937",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textDecoration: "none",
                        }}
                      >
                        📜
                      </a>
                    )}

                    {/* ⋮ — always visible. Sits at the END of the row
                        so opening the menu pushes the extra icons in
                        between the blue View and the kebab itself. */}
                    <button
                      className="btn-icon"
                      title="More actions"
                      onClick={() =>
                        setMenuRow(menuRow === i ? null : i)
                      }
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "22px",
                        lineHeight: 1,
                        padding: "0 4px",
                        color: "#e5e5e5",
                      }}
                    >
                      ⋮
                    </button>
                  </div>
                </div>

                {/* EXPAND */}
                {openRow === i && (
                  <div
                    style={{
                      padding: "8px 0 12px 0",
                      fontSize: "13px",
                      color: "#ccc",
                    }}
                  >
                    {/* Template type: render one "Signed by …" line
                        per signer from the snapshot captured on the
                        history entry. Falls through to the legacy
                        single "Submitted by" line for Certificate /
                        Info documents (which don't carry signers). */}
                    {documentType === "template" &&
                    Array.isArray(item.signers) &&
                    item.signers.length > 0
                      ? item.signers.map((s: any, sIdx: number) => (
                          <div
                            key={sIdx}
                            style={sIdx > 0 ? { marginTop: "4px" } : undefined}
                          >
                            Signed by{" "}
                            <span className="smrtdocs-user-highlight">
                              {s?.name || "—"}
                            </span>
                            {s?.signedAt && (
                              <>
                                {" "}
                                •{" "}
                                {new Date(s.signedAt).toLocaleString()}
                              </>
                            )}
                          </div>
                        ))
                      : (item.submittedBy || item.approvedBy) &&
                        item.submittedAt && (
                          <div>
                            Submitted by{" "}
                            <span className="smrtdocs-user-highlight">
                              {isUserView
                                ? (item.approvedBy ? (item.submittedBy || "Admin") : (item.status === "Client Submitted" ? (item.submittedBy || "You") : "Admin"))
                                : (item.submittedBy || item.approvedBy || "—")}
                            </span>{" "}
                            • {new Date(item.submittedAt).toLocaleString()}
                          </div>
                        )}

                    {item.approvedBy && item.approvedAt && (
                      <div style={{ marginTop: "4px" }}>
                        Approved by {isUserView ? "Admin" : item.approvedBy} •{" "}
                        {new Date(item.approvedAt).toLocaleString()}
                      </div>
                    )}

                    {item.archivedAt && (
                      <div style={{ marginTop: "4px" }}>
                        Archived on {new Date(item.archivedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
